import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  getUsageReport,
  exportAdminCsv,
  type DailyPoint,
  type TopInstitution,
} from "@/lib/admin.reports.functions";
import { toast } from "sonner";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";


export const Route = createFileRoute("/admin/reports")({
  component: AdminReportsPage,
});

type ExportKind =
  | "users"
  | "reviews"
  | "signals"
  | "change_requests"
  | "marker_feedback"
  | "critique_runs";

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function AdminReportsPage() {
  const reportFn = useServerFn(getUsageReport);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin", "usage-report"],
    queryFn: () => reportFn(),
  });

  // Export filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [reviewStatus, setReviewStatus] = useState("");

  const filterPayload = useMemo(() => {
    const out: { dateFrom?: string; dateTo?: string; flaggedOnly?: boolean; status?: string } = {};
    if (dateFrom) out.dateFrom = new Date(dateFrom + "T00:00:00Z").toISOString();
    if (dateTo) out.dateTo = new Date(dateTo + "T23:59:59Z").toISOString();
    if (flaggedOnly) out.flaggedOnly = true;
    if (reviewStatus) out.status = reviewStatus;
    return out;
  }, [dateFrom, dateTo, flaggedOnly, reviewStatus]);

  const exportFn = useServerFn(exportAdminCsv);
  const exportMut = useMutation({
    mutationFn: (kind: ExportKind) =>
      exportFn({
        data: {
          kind,
          ...(kind === "users" ? { flaggedOnly: filterPayload.flaggedOnly } : {}),
          ...(kind === "reviews" || kind === "change_requests"
            ? { status: filterPayload.status }
            : {}),
          dateFrom: filterPayload.dateFrom,
          dateTo: filterPayload.dateTo,
        },
      }),
    onSuccess: ({ filename, csv, rows }) => {
      downloadCsv(filename, csv);
      toast.success(`Exported ${rows.toLocaleString()} row${rows === 1 ? "" : "s"}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-10">
      <section>
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <p className="overline mb-2">Activity</p>
            <h2 className="font-serif text-2xl text-primary">Usage report</h2>
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="text-xs px-3 py-1.5 border border-border hover:bg-muted disabled:opacity-40"
          >
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {isLoading && <p className="text-sm text-ink-tertiary">Loading…</p>}
        {error && (
          <p className="text-sm text-destructive">
            {error instanceof Error ? error.message : "Failed to load report"}
          </p>
        )}

        {data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <Stat label="Total users" value={data.totals.users} hint={`${data.recent.usersLast7d} new in 7d`} />
              <Stat label="Active (30d)" value={data.totals.activeUsers30d} hint={`${data.totals.activeUsers7d} in 7d`} />
              <Stat label="Flagged users" value={data.totals.flaggedUsers} />
              <Stat label="Reviews" value={data.totals.reviews} hint={`${data.recent.reviewsLast7d} new in 7d`} />
              <Stat
                label="Critiques generated"
                value={data.totals.critiquesGenerated}
                hint={`${data.recent.critiquesLast7d} in 7d`}
              />
              <Stat
                label="Full documents analysed"
                value={data.totals.fullDocumentAnalysed}
                hint={`${data.recent.fullDocsLast7d} in 7d`}
              />
              <Stat
                label="Marker feedback"
                value={data.totals.markerFeedbackSubmitted}
                hint={`${data.recent.markerFeedbackLast7d} in 7d`}
              />
              <Stat label="Open signals" value={data.totals.openSignals} />
              <Stat label="Pending change requests" value={data.totals.pendingChangeRequests} />
            </div>

            <div className="mt-8">
              <p className="overline mb-2">AI usage (last 30 days)</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <Stat label="Critique runs" value={data.ai.runsLast30d} hint={`${data.ai.runsLast7d} in 7d`} />
                <Stat label="Tokens used" value={data.ai.tokensUsed} hint={`${data.ai.tokensLast7d.toLocaleString("en-GB")} in 7d`} />
                <Stat label="Avg tokens / run" value={data.ai.avgTokensPerRun} />
                <Stat label="Avg latency" value={`${data.ai.avgLatencyMs} ms`} />
                <Stat
                  label="Tokens / active user"
                  value={
                    data.totals.activeUsers30d > 0
                      ? Math.round(data.ai.tokensUsed / data.totals.activeUsers30d)
                      : 0
                  }
                />
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <DailyTrendCard
                title="Critique volume (30d)"
                series={data.daily}
                pick={(d) => d.critiques}
                accent="bg-primary"
              />
              <DailyTrendCard
                title="AI tokens / day (30d)"
                series={data.daily}
                pick={(d) => d.tokens}
                accent="bg-amber-500"
              />
              <DailyTrendCard
                title="New users / day"
                series={data.daily}
                pick={(d) => d.newUsers}
                accent="bg-emerald-600"
              />
              <DailyTrendCard
                title="New reviews / day"
                series={data.daily}
                pick={(d) => d.newReviews}
                accent="bg-sky-600"
              />
            </div>

            <div className="mt-8">
              <AiUsageTrendChart series={data.daily} />
            </div>

            <div className="mt-8">
              <p className="overline mb-2">Top institutions</p>
              <TopInstitutionsTable items={data.topInstitutions} />
            </div>


            <p className="text-xs font-mono text-ink-tertiary mt-6">
              Generated {new Date(data.generated_at).toLocaleString("en-GB")}
            </p>
          </>
        )}
      </section>

      <section>
        <p className="overline mb-2">Exports</p>
        <h2 className="font-serif text-2xl text-primary mb-1">Download CSV</h2>
        <p className="text-sm text-ink-secondary mb-4 max-w-2xl">
          Snapshots of operational tables, up to 10,000 rows each. Filters below apply to the
          relevant exports. Files are UTF-8 with BOM (Excel-friendly) and CSV-injection safe.
        </p>

        <div className="border border-border bg-card p-4 mb-4">
          <p className="overline mb-3">Filters</p>

          <div className="flex flex-wrap gap-2 mb-3">
            {[
              { label: "Last 7 days", days: 7 },
              { label: "Last 30 days", days: 30 },
              { label: "Last 90 days", days: 90 },
            ].map((preset) => {
              const active = isPresetActive(dateFrom, dateTo, preset.days);
              return (
                <button
                  key={preset.days}
                  type="button"
                  onClick={() => applyPreset(preset.days, setDateFrom, setDateTo)}
                  className={`text-xs px-3 py-1.5 border ${
                    active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border bg-background hover:bg-muted"
                  }`}
                >
                  {preset.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => {
                setDateFrom("");
                setDateTo("");
              }}
              className="text-xs px-3 py-1.5 border border-border bg-background hover:bg-muted"
            >
              All time
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
            <label className="block">
              <span className="overline block mb-1">From (created_at)</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full border border-border bg-background px-2 py-1.5"
              />
            </label>
            <label className="block">
              <span className="overline block mb-1">To (created_at)</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full border border-border bg-background px-2 py-1.5"
              />
            </label>
            <label className="block">
              <span className="overline block mb-1">Review / request status</span>
              <input
                type="text"
                value={reviewStatus}
                onChange={(e) => setReviewStatus(e.target.value)}
                placeholder="e.g. pending, complete"
                className="w-full border border-border bg-background px-2 py-1.5"
              />
            </label>
            <label className="flex items-end gap-2 text-sm pb-1">
              <input
                type="checkbox"
                checked={flaggedOnly}
                onChange={(e) => setFlaggedOnly(e.target.checked)}
              />
              <span>Users: flagged only</span>
            </label>
          </div>
          {(dateFrom || dateTo || flaggedOnly || reviewStatus) && (
            <button
              type="button"
              onClick={() => {
                setDateFrom("");
                setDateTo("");
                setFlaggedOnly(false);
                setReviewStatus("");
              }}
              className="mt-3 text-xs text-ink-tertiary hover:text-primary"
            >
              Clear filters
            </button>
          )}
        </div>


        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ExportCard
            title="Users"
            description="Profiles: name, email, institution, flags, support notes, timestamps."
            onClick={() => exportMut.mutate("users")}
            pending={exportMut.isPending && exportMut.variables === "users"}
          />
          <ExportCard
            title="Reviews"
            description="All review submissions with status, course, attestation, token usage."
            onClick={() => exportMut.mutate("reviews")}
            pending={exportMut.isPending && exportMut.variables === "reviews"}
          />
          <ExportCard
            title="Critique runs"
            description="AI runs with tokens, latency, and parent-run lineage."
            onClick={() => exportMut.mutate("critique_runs")}
            pending={exportMut.isPending && exportMut.variables === "critique_runs"}
          />
          <ExportCard
            title="Marker feedback"
            description="Consented marker comments and final grades for the learning loop."
            onClick={() => exportMut.mutate("marker_feedback")}
            pending={exportMut.isPending && exportMut.variables === "marker_feedback"}
          />
          <ExportCard
            title="Account activity signals"
            description="Integrity signals raised against accounts, with review state."
            onClick={() => exportMut.mutate("signals")}
            pending={exportMut.isPending && exportMut.variables === "signals"}
          />
          <ExportCard
            title="Institution change requests"
            description="User-submitted institution changes and their resolution."
            onClick={() => exportMut.mutate("change_requests")}
            pending={exportMut.isPending && exportMut.variables === "change_requests"}
          />
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  const display = typeof value === "number" ? value.toLocaleString("en-GB") : value;
  return (
    <div className="border border-border bg-card p-4">
      <p className="overline mb-1">{label}</p>
      <p className="font-serif text-3xl text-primary tabular-nums">{display}</p>
      {hint && <p className="text-xs text-ink-tertiary mt-1 font-mono">{hint}</p>}
    </div>
  );
}

function DailyTrendCard({
  title,
  series,
  pick,
  accent,
}: {
  title: string;
  series: DailyPoint[];
  pick: (d: DailyPoint) => number;
  accent: string;
}) {
  const values = series.map(pick);
  const max = Math.max(1, ...values);
  const total = values.reduce((s, v) => s + v, 0);
  const peak = Math.max(0, ...values);
  return (
    <div className="border border-border bg-card p-4">
      <div className="flex items-baseline justify-between mb-3">
        <p className="overline">{title}</p>
        <p className="text-xs font-mono text-ink-tertiary">
          Σ {total.toLocaleString("en-GB")} · peak {peak.toLocaleString("en-GB")}
        </p>
      </div>
      <div className="flex items-end gap-[2px] h-24" role="img" aria-label={title}>
        {series.map((d) => {
          const v = pick(d);
          const h = Math.max(2, Math.round((v / max) * 96));
          return (
            <div
              key={d.date}
              title={`${d.date}: ${v.toLocaleString("en-GB")}`}
              className={`flex-1 ${accent} opacity-80 hover:opacity-100`}
              style={{ height: `${h}px` }}
            />
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] font-mono text-ink-tertiary mt-1">
        <span>{series[0]?.date.slice(5)}</span>
        <span>{series[series.length - 1]?.date.slice(5)}</span>
      </div>
    </div>
  );
}

function TopInstitutionsTable({ items }: { items: TopInstitution[] }) {
  if (items.length === 0) {
    return (
      <div className="border border-border bg-card p-4 text-sm text-ink-tertiary">
        No institutions recorded yet.
      </div>
    );
  }
  const maxUsers = Math.max(1, ...items.map((i) => i.users));
  return (
    <div className="border border-border bg-card overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs uppercase tracking-wide text-ink-tertiary">
          <tr>
            <th className="text-left p-3">Institution</th>
            <th className="text-right p-3 w-24">Users</th>
            <th className="text-right p-3 w-24">Reviews</th>
            <th className="text-left p-3 w-1/3">Share</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.name} className="border-t border-border">
              <td className="p-3">{i.name}</td>
              <td className="p-3 text-right font-mono tabular-nums">{i.users}</td>
              <td className="p-3 text-right font-mono tabular-nums">{i.reviews}</td>
              <td className="p-3">
                <div className="h-2 bg-muted">
                  <div
                    className="h-2 bg-primary"
                    style={{ width: `${Math.round((i.users / maxUsers) * 100)}%` }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExportCard({
  title,
  description,
  onClick,
  pending,
}: {
  title: string;
  description: string;
  onClick: () => void;
  pending: boolean;
}) {
  return (
    <div className="border border-border bg-card p-4 flex items-start justify-between gap-4">
      <div>
        <p className="font-serif text-lg text-primary mb-1">{title}</p>
        <p className="text-xs text-ink-secondary">{description}</p>
      </div>
      <button
        type="button"
        onClick={onClick}
        disabled={pending}
        className="shrink-0 bg-primary text-primary-foreground text-xs px-3 py-2 hover:opacity-90 disabled:opacity-50"
      >
        {pending ? "Preparing…" : "Download CSV"}
      </button>
    </div>
  );
}

// ---------- Date-preset helpers ----------

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function applyPreset(
  days: number,
  setFrom: (v: string) => void,
  setTo: (v: string) => void,
) {
  const to = new Date();
  const from = new Date(to.getTime() - (days - 1) * 86400000);
  setFrom(toIsoDate(from));
  setTo(toIsoDate(to));
}

function isPresetActive(from: string, to: string, days: number): boolean {
  if (!from || !to) return false;
  const today = toIsoDate(new Date());
  if (to !== today) return false;
  const expected = toIsoDate(new Date(Date.now() - (days - 1) * 86400000));
  return from === expected;
}

// ---------- AI Usage Trend (line chart) ----------

function AiUsageTrendChart({ series }: { series: DailyPoint[] }) {
  const rows = series.map((d) => ({
    date: d.date.slice(5),
    Critiques: d.critiques,
    "Full documents": d.fullDocs,
    Tokens: d.tokens,
  }));
  return (
    <div className="border border-border bg-card p-4">
      <div className="flex items-baseline justify-between mb-3">
        <p className="overline">AI usage trend (30d)</p>
        <p className="text-xs font-mono text-ink-tertiary">
          critiques · full documents · tokens
        </p>
      </div>
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 10 }}
              width={40}
              allowDecimals={false}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 10 }}
              width={50}
              tickFormatter={(v: number) =>
                v >= 1000 ? `${Math.round(v / 1000)}k` : String(v)
              }
            />
            <RechartsTooltip
              contentStyle={{ fontSize: 12 }}
              formatter={(v: number) => v.toLocaleString("en-GB")}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="Critiques"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              dot={false}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="Full documents"
              stroke="hsl(var(--accent-foreground, var(--primary)))"
              strokeWidth={2}
              strokeDasharray="4 4"
              dot={false}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="Tokens"
              stroke="#d97706"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
